import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import ApplyV2Picture from '@core/components/ApplyDialogV2/ApplyV2Picture'
import { useFormikContext } from 'formik'
import React, { useId, useState, useEffect } from 'react'
import getCurrencyById from '@core/utils/helpers/getCurrencyById'
import clsx from 'clsx'
import './ApplyV2InputNumber.scss'

interface Props {
    value?: number | string | null
    label?: string
    onChange?: (value: number | null | string) => void
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isLoading?: boolean
    errorMessage?: string | string[] | null | boolean
    children?: React.ReactNode
    isRequired?: boolean
    placeholder?: string
    autoFocus?: boolean
    min?: number
    max?: number
    decimals?: number | null | undefined
    isShowErrorMessage?: boolean
    currencyId?: string | number
    description?: React.ReactNode
}

const baseClass = 'ApplyV2InputNumber'

function ApplyV2InputNumber({
    value,
    label,
    onChange,
    onFocus,
    onBlur,
    isDisabled = false,
    className = '',
    id,
    name,
    isLoading = false,
    errorMessage,
    children,
    isRequired = false,
    placeholder = '',
    autoFocus,
    min,
    max,
    decimals = null,
    isShowErrorMessage = true,
    currencyId,
    description,
}: Props) {
    const defaultId = useId()
    // const [displayValue, setDisplayValue] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    // const formik = useFormikContext()

    // Format the value for display with fixed decimals
    // useEffect(() => {
    //     if (!isFocused) {
    //         const numValue =
    //             value !== null && value !== undefined ? (typeof value === 'number' ? value : parseFloat(value)) : NaN
    //
    //         if (!isNaN(numValue)) {
    //             // Always show specified number of decimal places, padding with zeros if needed
    //             const formatted = decimals > 0 ? numValue.toFixed(decimals) : numValue.toString()
    //             setDisplayValue(formatted)
    //         } else {
    //             setDisplayValue('')
    //         }
    //     }
    // }, [value, decimals, isFocused])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        if (onChange) {
            onChange(inputValue.trim() ? inputValue : null)
        }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false)

        if (onBlur) {
            // console.log('handleBlur', value)
            if (value || value === 0) {
                if (decimals !== null && decimals !== undefined) {
                    const val = Number(value || 0).toFixed(decimals)
                    onChange && onChange(val)
                } else {
                    // Если decimals не задан, передаем значение как есть
                    onChange && onChange(value)
                }
            } else {
                onChange && onChange(null)
            }
            onBlur(e)
        }
    }

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
            })}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : defaultId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}
            <div
                className={clsx(`${baseClass}__input-wrapper`, {
                    [`${className}__input-wrapper`]: className,
                    [`${baseClass}__input-wrapper--disabled`]: isDisabled,
                    [`${baseClass}__input-wrapper--loading`]: isLoading,
                    [`${baseClass}__input-wrapper--error`]: !!errorMessage,
                })}
            >
                {getCurrencyById(currencyId) && !isLoading && (
                    <ApplyV2Picture
                        className={`${baseClass}__input-currency`}
                        fallbackSrc={
                            `${window.location.protocol}//${window.location.hostname}/${getCurrencyById(currencyId)?.img}` ??
                            ''
                        }
                        alt={'dd'}
                    />
                )}

                <input
                    type="number"
                    inputMode={decimals > 0 ? 'decimal' : 'numeric'}
                    id={id ? id.toString() : defaultId}
                    min={min}
                    max={max}
                    name={name}
                    value={value ?? ''}
                    onChange={handleChange}
                    onFocus={(e) => {
                        setIsFocused(true)
                        onFocus?.(e)
                    }}
                    onBlur={handleBlur}
                    disabled={isDisabled}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    className={clsx(`${baseClass}__input`, {
                        [`${baseClass}__input--error`]: !!errorMessage,
                        [`${baseClass}__input--loading`]: isLoading,
                    })}
                />
            </div>
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div className={clsx(`${baseClass}__error`, { [`${className}__error`]: className })}>
                    <div className={clsx(`${baseClass}__error--icon`, { [`${className}__error--icon`]: className })}>
                        <WarningErrorIcon />
                    </div>
                    <div className={clsx(`${baseClass}__error--text`, { [`${className}__error--text`]: className })}>
                        {errorMessage}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2InputNumber
